<template>
  <v-container fluid>
    <v-row>
      <!-- Agents List -->
      <v-col cols="12" md="4">
        <v-card>
          <v-card-title class="primary white--text">
            <v-icon left dark>mdi-robot</v-icon>
            Agents
            <v-spacer></v-spacer>
            <v-btn icon dark @click="createNewAgent">
              <v-icon>mdi-plus</v-icon>
            </v-btn>
          </v-card-title>
          <v-card-text>
            <v-list>
              <v-list-item
                v-for="agent in agents"
                :key="agent.uuid"
                @click="selectAgent(agent)"
                :class="{ 'blue lighten-5': selectedAgent && selectedAgent.uuid === agent.uuid }"
              >
                <v-list-item-content>
                  <v-list-item-title>{{ agent.name }}</v-list-item-title>
                  <v-list-item-subtitle>
                    Module: {{ agent.module_name || 'N/A' }}
                  </v-list-item-subtitle>
                </v-list-item-content>
                <v-list-item-action>
                  <v-chip small :color="agent.cheap ? 'green' : 'blue'" dark>
                    {{ agent.cheap ? 'Cheap' : 'Full' }}
                  </v-chip>
                </v-list-item-action>
              </v-list-item>
            </v-list>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Agent Editor -->
      <v-col cols="12" md="8">
        <v-card v-if="selectedAgent">
          <v-card-title class="secondary white--text">
            <v-icon left dark>mdi-pencil</v-icon>
            {{ isNewAgent ? 'New Agent' : 'Edit Agent' }}
          </v-card-title>
          <v-card-text>
            <v-form ref="form" v-model="valid">
              <v-text-field
                v-model="selectedAgent.name"
                label="Agent Name"
                outlined
                :rules="[v => !!v || 'Name is required']"
              ></v-text-field>

              <v-select
                v-model="selectedAgent.module_id"
                :items="modules"
                item-text="name"
                item-value="id"
                label="Module"
                outlined
                :rules="[v => !!v || 'Module is required']"
              >
                <template v-slot:item="{ item }">
                  <div>
                    <div class="font-weight-bold">{{ item.name }}</div>
                    <div class="text-caption grey--text">
                      Workflows: {{ item.workflow_templates ? item.workflow_templates.length : 0 }}
                    </div>
                  </div>
                </template>
              </v-select>

              <v-textarea
                v-model="selectedAgent.system_prompt"
                label="System Prompt"
                outlined
                rows="10"
                hint="Instructions for the AI agent"
                persistent-hint
              ></v-textarea>

              <v-row>
                <v-col cols="12" md="6">
                  <v-switch
                    v-model="selectedAgent.cheap"
                    label="Use Cheap Model (Haiku)"
                    hint="Toggle to use cheaper/faster model"
                    persistent-hint
                  ></v-switch>
                </v-col>
                <v-col cols="12" md="6">
                  <v-switch
                    v-model="selectedAgent.reason"
                    label="Enable Reasoning"
                    hint="Use extended thinking mode"
                    persistent-hint
                  ></v-switch>
                </v-col>
              </v-row>

              <v-alert v-if="selectedAgent.uuid" type="info" dense text class="mt-4">
                <strong>Agent UUID:</strong> {{ selectedAgent.uuid }}
              </v-alert>
            </v-form>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text @click="cancelEdit">Cancel</v-btn>
            <v-btn
              color="primary"
              :loading="saving"
              :disabled="!valid"
              @click="saveAgent"
            >
              Save Agent
            </v-btn>
          </v-card-actions>
        </v-card>

        <v-card v-else>
          <v-card-text class="text-center grey--text">
            <v-icon size="64" color="grey lighten-1">mdi-robot-outline</v-icon>
            <div class="mt-4">Select an agent or create a new one</div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Snackbar for notifications -->
    <v-snackbar v-model="snackbar" :color="snackbarColor" :timeout="3000">
      {{ snackbarText }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar = false">Close</v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script>
import { query_dispatcher_get, sync_command_dispatcher } from '@/api/client'

export default {
  name: 'AgentMaker',
  data() {
    return {
      agents: [],
      modules: [],
      selectedAgent: null,
      isNewAgent: false,
      valid: false,
      saving: false,
      snackbar: false,
      snackbarText: '',
      snackbarColor: 'success',
      query_dispatcher: query_dispatcher_get,
      command_dispatcher: sync_command_dispatcher
    }
  },
  mounted() {
    this.loadAgents()
    this.loadModules()
  },
  methods: {
    async loadAgents() {
      try {
        const response = await this.query_dispatcher.dispatch('get.all.agents', {})
        this.agents = response
      } catch (error) {
        console.error('Error loading agents:', error)
        this.showSnackbar('Error loading agents', 'error')
      }
    },
    async loadModules() {
      try {
        const response = await this.query_dispatcher.dispatch('modules.get_all', { with_details: false })
        this.modules = response.data
      } catch (error) {
        console.error('Error loading modules:', error)
        this.showSnackbar('Error loading modules', 'error')
      }
    },
    selectAgent(agent) {
      this.selectedAgent = JSON.parse(JSON.stringify(agent))
      this.isNewAgent = false
    },
    createNewAgent() {
      this.selectedAgent = {
        uuid: null,
        name: '',
        module_id: null,
        system_prompt: '',
        cheap: false,
        reason: false
      }
      this.isNewAgent = true
    },
    cancelEdit() {
      this.selectedAgent = null
      this.isNewAgent = false
    },
    async saveAgent() {
      if (!this.$refs.form.validate()) return

      this.saving = true
      try {
        const response = await this.command_dispatcher.dispatch('save.agent', {
          uuid: this.selectedAgent.uuid,
          name: this.selectedAgent.name,
          module_id: this.selectedAgent.module_id,
          system_prompt: this.selectedAgent.system_prompt,
          cheap: this.selectedAgent.cheap,
          reason: this.selectedAgent.reason
        })

        this.showSnackbar('Agent saved successfully', 'success')
        await this.loadAgents()

        // Select the saved agent
        const savedAgent = this.agents.find(a => a.uuid === response.uuid)
        if (savedAgent) {
          this.selectAgent(savedAgent)
        }
      } catch (error) {
        console.error('Error saving agent:', error)
        const errorMsg = error && error.message ? error.message : 'Unknown error'
        this.showSnackbar('Error saving agent: ' + errorMsg, 'error')
      } finally {
        this.saving = false
      }
    },
    showSnackbar(text, color = 'success') {
      this.snackbarText = text
      this.snackbarColor = color
      this.snackbar = true
    }
  }
}
</script>

<style scoped>
.v-list-item {
  cursor: pointer;
}
</style>
